// Sprint 12 (D-008) — binary Working / Idle input model.
//
// "Input is always writable." The chat composer is disabled only in the
// genuinely unrecoverable case (error + no claudeSessionId). Every other
// state either writes immediately or triggers a transparent respawn.

import type { Agent, AgentStatus } from '../types';
import { t } from '../i18n';

export type InputBlockedReason =
  | 'none'
  | 'unrecoverable'
  // Oficina v2 (S1, T1.2): archivado = cerrado. El servidor lo rechaza (AGENT_ARCHIVED) y aquí la
  // caja ni siquiera se ofrece: escribir a un archivado ya no lo despierta.
  | 'archived';

export function computeInputBlocked(
  agent: Pick<Agent, 'status' | 'claudeSessionId'> & { archived?: boolean },
): InputBlockedReason {
  if (agent.archived) return 'archived';
  if (agent.status === 'error' && !agent.claudeSessionId) {
    return 'unrecoverable';
  }
  return 'none';
}

export function isInputBlocked(agent: Pick<Agent, 'status' | 'claudeSessionId'> & { archived?: boolean }): boolean {
  return computeInputBlocked(agent) !== 'none';
}

export interface PlaceholderContext {
  status: AgentStatus;
  hasSession: boolean;
  isDisconnected?: boolean;
  isArchived?: boolean;
  hasPermissionPending?: boolean;
}

export function placeholderForStatus(
  statusOrCtx: AgentStatus | PlaceholderContext,
  hasSession?: boolean,
): string {
  if (typeof statusOrCtx === 'string') {
    return placeholderForContext({ status: statusOrCtx, hasSession: hasSession ?? false });
  }
  return placeholderForContext(statusOrCtx);
}

// Sprint 12 — collapsed to binary: Working = "Add a follow-up…",
// Idle = "Send a message…". Priority overrides preserved.
function placeholderForContext(ctx: PlaceholderContext): string {
  if (ctx.isDisconnected) return t('composer.placeholder.disconnected');
  if (ctx.isArchived) return t('composer.placeholder.archived');
  if (ctx.hasPermissionPending) return t('composer.placeholder.permissionPending');

  if (ctx.status === 'running') {
    return t('composer.placeholder.running');
  }

  if (ctx.status === 'error' && !ctx.hasSession) {
    return t('composer.placeholder.errorUnrecoverable');
  }

  return t('composer.placeholder.idle');
}

/**
 * Kept for API compatibility. Sprint 12 removes all visible hints —
 * the composer being enabled is the clearest signal.
 */
export function hintForStatus(_status: AgentStatus): string | null {
  return null;
}
